import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Trophy, Loader2, Music, CheckCircle, PartyPopper,
  Volume2, VolumeX, Skull, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { API_URL } from '../utils/api';

export default function BingoPlayPage() {
  const { gameCode } = useParams();
  const navigate = useNavigate();
  
  const [nickname, setNickname] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [card, setCard] = useState(null);
  const [markedDances, setMarkedDances] = useState([]);
  const [calledDances, setCalledDances] = useState([]);
  const [gameStatus, setGameStatus] = useState('waiting');
  const [winPattern, setWinPattern] = useState('line');
  const [winners, setWinners] = useState([]);
  const [joining, setJoining] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastCalledDance, setLastCalledDance] = useState(null);
  const [hasWon, setHasWon] = useState(false);
  const [isEliminated, setIsEliminated] = useState(false);

  // Poll for game updates
  const fetchPlayerState = useCallback(async () => {
    if (!playerId || !gameCode) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/card/${playerId}`);
      if (res.ok) {
        const data = await res.json();
        
        // Check for new dance called
        if (data.called_dances?.length > calledDances.length) {
          const newDance = data.called_dances[data.called_dances.length - 1];
          setLastCalledDance(newDance);
          
          // Speak the dance name
          if (soundEnabled && 'speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(newDance);
            utterance.rate = 0.9;
            speechSynthesis.speak(utterance);
          }
          
          // For OGNIB: Check if this dance just eliminated the player
          if (data.win_pattern === 'ognib' && data.eliminated && !isEliminated) {
            const cardDances = data.card.flat();
            if (cardDances.includes(newDance)) {
              toast.error("You've been eliminated!", { duration: 5000 });
            }
          }
        }
        
        setCard(data.card);
        setMarkedDances(data.marked);
        setCalledDances(data.called_dances || []);
        setGameStatus(data.status);
        setWinPattern(data.win_pattern);
        setWinners(data.winners || []);
        setIsEliminated(data.eliminated || false);
        
        // Check if we won
        if (data.winners?.some(w => w.player_id === playerId) && !hasWon) {
          setHasWon(true);
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching state:', error);
    }
  }, [playerId, gameCode, calledDances.length, markedDances, soundEnabled, hasWon, isEliminated]);

  useEffect(() => {
    if (!playerId) return;
    
    fetchPlayerState();
    const interval = setInterval(fetchPlayerState, 1500);
    return () => clearInterval(interval);
  }, [playerId, fetchPlayerState]);

  // Load saved player from localStorage
  useEffect(() => {
    const savedPlayer = localStorage.getItem(`bingo_player_${gameCode}`);
    if (savedPlayer) {
      const { playerId: savedId, nickname: savedNick } = JSON.parse(savedPlayer);
      setPlayerId(savedId);
      setNickname(savedNick);
    }
  }, [gameCode]);

  const joinGame = async () => {
    if (!nickname.trim()) {
      toast.error('Please enter a nickname');
      return;
    }
    
    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: nickname.trim() })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPlayerId(data.player_id);
        setCard(data.card);
        setMarkedDances([]);
        
        // Save to localStorage
        localStorage.setItem(`bingo_player_${gameCode}`, JSON.stringify({
          playerId: data.player_id,
          nickname: nickname.trim()
        }));
        
        toast.success('Joined the game!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to join');
      }
    } catch (error) {
      toast.error('Failed to join game');
    } finally {
      setJoining(false);
    }
  };

  const markDance = async (dance) => {
    if (!calledDances.includes(dance)) {
      toast.error('This dance hasn\'t been called yet!');
      return;
    }
    if (markedDances.includes(dance)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/mark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId, dance_name: dance })
      });
      
      if (res.ok) {
        setMarkedDances(prev => [...prev, dance]);
      }
    } catch (error) {
      console.error('Error marking dance:', error);
    }
  };

  const claimWin = async () => {
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${gameCode}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId })
      });
      
      if (res.ok) {
        setHasWon(true);
        confetti({
          particleCount: 150,
          spread: 100,
          origin: { y: 0.6 }
        });
        toast.success('BINGO! You win!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Invalid bingo claim');
      }
    } catch (error) {
      toast.error('Failed to claim win');
    } finally {
      setClaiming(false);
    }
  };

  const checkPotentialWin = () => {
    if (!card || !markedDances) return false;
    
    const marked = new Set(markedDances);
    const numRows = card.length;  // 5
    const numCols = card[0]?.length || 3;  // 3
    
    if (winPattern === 'fullhouse') {
      return card.flat().every(cell => marked.has(cell));
    }
    
    // Line pattern: Only columns (5 down) count as a win
    for (let col = 0; col < numCols; col++) {
      if (card.every(row => marked.has(row[col]))) return true;
    }
    
    return false;
  };

  const canClaimWin = checkPotentialWin() && !hasWon && winners.length === 0;

  // Join form
  if (!playerId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-900/30 to-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Music className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">Join Bingo</h1>
              <p className="text-slate-400 mt-1">Game Code: <span className="font-mono text-violet-400">{gameCode}</span></p>
            </div>
            
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Enter your nickname"
              className="bg-slate-800 border-slate-700 text-white text-center text-lg h-12"
              maxLength={20}
              onKeyPress={(e) => e.key === 'Enter' && joinGame()}
            />
            
            <Button
              onClick={joinGame}
              disabled={joining}
              className="w-full bg-violet-600 hover:bg-violet-500 h-12 text-lg"
            >
              {joining ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Join Game'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OGNIB Winner screen (last one standing)
  if (hasWon && winPattern === 'ognib') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Trophy className="w-24 h-24 mx-auto text-red-400 mb-4 animate-bounce" />
          <h1 className="text-5xl font-bold text-white mb-2">SURVIVOR!</h1>
          <p className="text-2xl text-red-400">Congratulations, {nickname}!</p>
          <p className="text-slate-400 mt-4">You're the last one standing!</p>
        </div>
      </div>
    );
  }

  // Regular Bingo Winner screen
  if (hasWon) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <PartyPopper className="w-24 h-24 mx-auto text-yellow-400 mb-4 animate-bounce" />
          <h1 className="text-5xl font-bold text-white mb-2">BINGO!</h1>
          <p className="text-2xl text-yellow-400">Congratulations, {nickname}!</p>
          <p className="text-slate-400 mt-4">You won the game!</p>
        </div>
      </div>
    );
  }

  // OGNIB Eliminated screen
  if (isEliminated && winPattern === 'ognib') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Skull className="w-20 h-20 mx-auto text-red-500 mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">You're Out!</h1>
          <p className="text-xl text-red-400">{lastCalledDance}</p>
          <p className="text-slate-400 mt-2">was on your card</p>
          <p className="text-slate-400 mt-6">Waiting for the game to end...</p>
          {winners.length > 0 && (
            <div className="mt-4 p-4 bg-red-900/30 rounded-lg">
              <Trophy className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
              <p className="text-yellow-400 font-bold">{winners[0].nickname} survived!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Someone else won (regular or OGNIB)
  if (winners.length > 0 && !hasWon) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-900/30 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <Trophy className="w-20 h-20 mx-auto text-yellow-400 mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Game Over!</h1>
          <p className="text-xl text-yellow-400">{winners[0].nickname} {winPattern === 'ognib' ? 'survived!' : 'wins!'}</p>
          <p className="text-slate-400 mt-4">Better luck next round!</p>
        </div>
      </div>
    );
  }

  // Game card
  return (
    <div className={`min-h-screen p-2 sm:p-4 ${winPattern === 'ognib' ? 'bg-slate-950' : 'bg-slate-950'}`}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold text-white">{nickname}</h1>
            <p className="text-xs text-slate-400">
              {winPattern === 'line' ? 'Get 5 down to win!' : 
               winPattern === 'ognib' ? 'AVOID the called dances!' : 'Fill all 15 squares!'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 text-slate-400 hover:text-white"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <Badge className={`${
              winPattern === 'ognib' 
                ? (gameStatus === 'active' ? 'bg-red-600' : 'bg-yellow-600')
                : (gameStatus === 'active' ? 'bg-green-600' : 'bg-yellow-600')
            }`}>
              {winPattern === 'ognib' ? 'OGNIB' : (gameStatus === 'active' ? 'Playing' : 'Waiting')}
            </Badge>
          </div>
        </div>

        {/* OGNIB Warning Banner */}
        {winPattern === 'ognib' && gameStatus === 'active' && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-2 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
            <span className="text-xs text-red-200">
              If a called dance is on your card, you're OUT!
            </span>
          </div>
        )}

        {/* Current Dance Banner */}
        {lastCalledDance && gameStatus === 'active' && (
          <div className={`rounded-lg p-3 mb-3 text-center animate-pulse ${
            winPattern === 'ognib' ? 'bg-red-600' : 'bg-violet-600'
          }`}>
            <div className={`text-xs ${winPattern === 'ognib' ? 'text-red-200' : 'text-violet-200'}`}>
              {winPattern === 'ognib' ? 'DANGER DANCE' : 'JUST CALLED'}
            </div>
            <div className="text-xl font-bold text-white">{lastCalledDance}</div>
          </div>
        )}

        {/* Claim Win Button (only for regular bingo) */}
        {canClaimWin && winPattern !== 'ognib' && (
          <Button
            onClick={claimWin}
            disabled={claiming}
            className="w-full mb-3 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold h-14 text-xl animate-pulse"
          >
            {claiming ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <Trophy className="w-6 h-6 mr-2" />
                CLAIM BINGO!
              </>
            )}
          </Button>
        )}

        {/* Bingo Card - 3 columns x 5 rows */}
        {card && (
          <div className={`rounded-xl p-2 sm:p-3 border ${
            winPattern === 'ognib' ? 'bg-slate-900 border-red-800/50' : 'bg-slate-900 border-slate-800'
          }`}>
            <div className="grid grid-cols-3 gap-2">
              {card.map((row, rowIdx) => (
                row.map((cell, colIdx) => {
                  const isMarked = markedDances.includes(cell);
                  const isCalled = calledDances.includes(cell);
                  const isDangerous = winPattern === 'ognib' && isCalled;
                  
                  return (
                    <button
                      key={`${rowIdx}-${colIdx}`}
                      onClick={() => winPattern !== 'ognib' && markDance(cell)}
                      disabled={winPattern === 'ognib' || isMarked || !isCalled}
                      className={`
                        h-16 sm:h-20 rounded-lg p-2 text-xs sm:text-sm font-medium
                        flex items-center justify-center text-center
                        transition-all duration-200
                        ${winPattern === 'ognib' ? (
                          isDangerous 
                            ? 'bg-red-600 text-white scale-95 border-2 border-red-400' 
                            : 'bg-slate-800 text-slate-300'
                        ) : (
                          isMarked ? 'bg-green-600 text-white scale-95' :
                          isCalled ? 'bg-yellow-500/20 text-yellow-300 border-2 border-yellow-500 animate-pulse' :
                          'bg-slate-800 text-slate-300'
                        )}
                      `}
                    >
                      {winPattern === 'ognib' && isDangerous ? (
                        <div className="flex flex-col items-center">
                          <Skull className="w-6 h-6 mb-0.5" />
                          <span className="text-[10px] line-clamp-1">{cell}</span>
                        </div>
                      ) : isMarked ? (
                        <CheckCircle className="w-8 h-8" />
                      ) : (
                        <span className="line-clamp-2 leading-tight">{cell}</span>
                      )}
                    </button>
                  );
                })
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-3 flex justify-center gap-4 text-xs text-slate-400">
          {winPattern === 'ognib' ? (
            <>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-600"></div>
                <span>Called (danger!)</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-slate-800"></div>
                <span>Safe</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-500/20 border border-yellow-500"></div>
                <span>Tap to mark</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-green-600"></div>
                <span>Marked</span>
              </div>
            </>
          )}
        </div>

        {/* Called Count */}
        <div className="mt-3 text-center text-sm text-slate-400">
          {calledDances.length} dances called
        </div>
      </div>
    </div>
  );
}
